/**
 * iOS Safari mobile detection.
 *
 * Detecting iOS reliably is harder than it should be in 2026:
 * - iPadOS 13+ ships a desktop user agent by default, so `iPad` no longer
 *   appears in navigator.userAgent.
 * - Mac Catalyst on iPad reports as macOS-Safari with touch support.
 * - Some Safari Technology Preview builds add "FxiOS"/"CriOS" wrappers.
 *
 * The compound heuristic below is the most reliable pattern as of iOS 17/18:
 *   1. Direct UA match for iPhone/iPod (still reliable on phones)
 *   2. macOS-Safari + maxTouchPoints > 1 (this is how iPadOS 13+ presents)
 *
 * Returns true for: iPhone, iPod, iPad (any iPadOS 13+ on Safari).
 * Returns false for: real macOS Safari (no touch), Android, anything else.
 */
export function isIOSSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPod/.test(ua)) return true;
  // iPad-as-Mac: macOS UA but with touch
  const macish = /Macintosh/.test(ua);
  const touchy = (navigator.maxTouchPoints ?? 0) > 1;
  if (macish && touchy) return true;
  return false;
}

/** Sub-detector: phone form factor specifically (excludes iPad). */
export function isIOSPhone(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPod/.test(navigator.userAgent);
}
